import React, { useMemo, useState } from 'react';
import { useAutomationTemplates } from '../../hooks/useAutomations';
import { useToast } from '../../components/Toast';

const emptyTemplate = () => ({
    id: '',
    name: '',
    channel: 'whatsapp',
    category: 'new_lead',
    body: '',
    language: 'en',
});

const SAMPLE_VARS = {
    '{lead.name}': 'Alex',
    '{lead.phone}': '+91 90000 00000',
    '{lead.email}': 'alex@example.com',
    '{lead.source}': 'website',
    '{agent.name}': 'Ishan',
    '{company.name}': 'AAuth Realty',
    '{followup.date}': '2026-02-02',
    '{followup.time}': '16:30',
    '{property.name}': 'Greenview Heights',
    '{property.location}': 'Gachibowli',
};

const renderPreview = (body = '') => (
    Object.entries(SAMPLE_VARS).reduce((value, [key, sample]) => (
        value.split(key).join(sample)
    ), body || '')
);

const AutomationTemplates = () => {
    const { templates, loading, create, update, remove, refetch } = useAutomationTemplates();
    const [selectedId, setSelectedId] = useState('');
    const [draft, setDraft] = useState(emptyTemplate());
    const [isNew, setIsNew] = useState(false);
    const { showSuccess, showError } = useToast();

    const CHANNEL_GROUPS = [
        { label: 'Messaging', options: ['whatsapp', 'sms', 'email', 'telegram'] },
        { label: 'Meta Social', options: ['instagram_dm', 'facebook_messenger'] },
        { label: 'Social Replies', options: ['instagram_comment_reply', 'facebook_comment_reply'] },
        { label: 'Other', options: ['linkedin_message', 'webhook'] },
    ];

    const CHANNEL_LABELS = {
        whatsapp: 'WhatsApp',
        sms: 'SMS',
        email: 'Email',
        telegram: 'Telegram',
        instagram_dm: 'Instagram DM',
        facebook_messenger: 'Facebook Messenger',
        instagram_comment_reply: 'Instagram Comment Reply',
        facebook_comment_reply: 'Facebook Comment Reply',
        linkedin_message: 'LinkedIn (manual)',
        webhook: 'Webhook',
    };

    const selected = useMemo(
        () => templates.find((item) => item.id === selectedId) || null,
        [templates, selectedId],
    );

    const handleSelect = (template) => {
        setSelectedId(template.id);
        setIsNew(false);
        setDraft({
            id: template.id,
            name: template.name || '',
            channel: template.channel || 'whatsapp',
            category: template.category || 'new_lead',
            body: template.body || '',
            language: template.language || 'en',
        });
    };

    const handleNew = () => {
        setSelectedId('');
        setIsNew(true);
        setDraft(emptyTemplate());
    };

    const handleSave = async () => {
        try {
            if (isNew) {
                const created = await create(draft);
                showSuccess('Template created');
                await refetch();
                handleSelect(created);
            } else {
                await update(draft.id, draft);
                showSuccess('Template updated');
                await refetch();
            }
        } catch (err) {
            showError(err.message || 'Failed to save template');
        }
    };

    const handleDelete = async () => {
        if (!draft.id) return;
        try {
            await remove(draft.id);
            showSuccess('Template deleted');
            await refetch();
            setDraft(emptyTemplate());
            setSelectedId('');
        } catch (err) {
            showError(err.message || 'Failed to delete template');
        }
    };

    return (
        <div className="crm-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px' }}>
            <div className="crm-card crm-card-hover">
                <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 className="crm-card-title">Templates</h2>
                    <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={handleNew}>+ New</button>
                </div>
                <div className="crm-card-body">
                    {loading ? (
                        <div className="crm-loading"><div className="crm-spinner" /></div>
                    ) : templates.length === 0 ? (
                        <div className="crm-empty-state">
                            <p>Create your first template.</p>
                            <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={handleNew}>
                                Create Template
                            </button>
                        </div>
                    ) : (
                        <div className="crm-list">
                            {templates.map((template) => (
                                <button
                                    key={template.id}
                                    type="button"
                                    className="crm-list-row"
                                    onClick={() => handleSelect(template)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        background: template.id === selectedId ? 'var(--crm-sidebar-hover)' : 'transparent',
                                        border: '1px solid transparent',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        marginBottom: '8px',
                                        color: '#fff',
                                    }}
                                >
                                    <div style={{ fontWeight: 600 }}>{template.name}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--crm-gray-500)' }}>
                                        {CHANNEL_LABELS[template.channel] || template.channel} • {template.category || 'general'}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="crm-card crm-card-hover">
                <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 className="crm-card-title">{isNew ? 'New Template' : 'Edit Template'}</h2>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {!isNew && draft.id && (
                            <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={handleDelete}>
                                Delete
                            </button>
                        )}
                        <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={handleSave}>
                            Save
                        </button>
                    </div>
                </div>
                <div className="crm-card-body" style={{ display: 'grid', gap: '16px' }}>
                    <div className="crm-form-grid">
                        <div className="crm-form-group">
                            <label className="crm-label">Name</label>
                            <input
                                className="crm-input"
                                value={draft.name}
                                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            />
                        </div>
                        <div className="crm-form-group">
                            <label className="crm-label">Channel</label>
                            <select
                                className="crm-select"
                                value={draft.channel}
                                onChange={(e) => setDraft({ ...draft, channel: e.target.value })}
                            >
                                {CHANNEL_GROUPS.map((group) => (
                                    <optgroup key={group.label} label={group.label}>
                                        {group.options.map((channel) => (
                                            <option key={channel} value={channel}>
                                                {CHANNEL_LABELS[channel] || channel}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>
                        <div className="crm-form-group">
                            <label className="crm-label">Category</label>
                            <select
                                className="crm-select"
                                value={draft.category}
                                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                            >
                                {['new_lead', 'follow_up', 'site_visit', 'docs', 'booking', 'win_back', 'general'].map((cat) => (
                                    <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
                                ))}
                            </select>
                        </div>
                        <div className="crm-form-group">
                            <label className="crm-label">Language</label>
                            <input
                                className="crm-input"
                                value={draft.language}
                                onChange={(e) => setDraft({ ...draft, language: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Body</label>
                        <textarea
                            className="crm-textarea"
                            rows={6}
                            value={draft.body}
                            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                        />
                    </div>

                    <div className="crm-card crm-card-subtle">
                        <div className="crm-card-header">
                            <h3 className="crm-card-title">Preview</h3>
                        </div>
                        <div className="crm-card-body">
                            <p style={{ whiteSpace: 'pre-line' }}>{renderPreview(draft.body)}</p>
                        </div>
                    </div>

                    <div className="crm-card crm-card-subtle">
                        <div className="crm-card-header">
                            <h3 className="crm-card-title">Available Variables</h3>
                        </div>
                        <div className="crm-card-body">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {Object.keys(SAMPLE_VARS).map((key) => (
                                    <span key={key} className="crm-tag-chip">{key}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AutomationTemplates;
